import { memo, useCallback, useState } from 'react'

import { AddCircle, ContentCopy, Delete, MoreVert, Refresh } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { PersonalAccessToken } from '@nativewaves/nw-cloud-sdk/identity'
import { useInfiniteQuery } from '@tanstack/react-query'
import { add, formatISO, formatDistanceToNow, intlFormatDistance, isValid, isWithinInterval, parseISO } from 'date-fns'
import { AnimatePresence, motion } from 'motion/react'
import CopyToClipboard from 'react-copy-to-clipboard'
import { useTranslation } from 'react-i18next'

import { CodeInline } from '@shared/components/MaterialUIEnhancements/CodeInline'
import { popInOut } from '@shared/utils/support'
import {
  usePersonalAccessTokenCreateMutation,
  usePersonalAccessTokenDeleteMutation,
} from 'hooks/mutations/identity/User'
import { userQueryKeys } from 'services/queryKeys/identity'
import { validEndDate, validStartDate } from 'utils'

const EXPIRY_OPTIONS = [
  { label: '7 days', value: 7 },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
  { label: '1 year', value: 365 },
  { label: 'No expiration', value: 0 },
] as const

type TokenRowProps = {
  token: PersonalAccessToken
}

const TokenRowRaw = ({ token }: TokenRowProps) => {
  const deleteMutation = usePersonalAccessTokenDeleteMutation()
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const handleDelete = useCallback(() => {
    deleteMutation.mutate({ userId: token.userId, tokenId: token.id })
    setMenuAnchor(null)
    setConfirmDelete(false)
  }, [deleteMutation, token.id, token.userId])

  const hasExpiry =
    isValid(parseISO(token.expiryTime)) &&
    isWithinInterval(parseISO(token.expiryTime), { start: validStartDate, end: validEndDate })

  const lastUsed =
    isValid(parseISO(token.lastUseTime)) &&
    isWithinInterval(parseISO(token.lastUseTime), { start: validStartDate, end: validEndDate })
      ? intlFormatDistance(parseISO(token.lastUseTime), new Date())
      : null

  const createdDate = isValid(parseISO(token.validFrom))
    ? formatDistanceToNow(parseISO(token.validFrom), { addSuffix: true })
    : null

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 0.75,
        px: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="heading-sm" noWrap>
          {token.name || 'Unnamed token'}
        </Typography>
        <Typography variant="text-sm" color="text.secondary" noWrap>
          {createdDate && `Created ${createdDate}`}
          {createdDate && ' · '}
          {hasExpiry
            ? `Expires ${formatDistanceToNow(parseISO(token.expiryTime), { addSuffix: true })}`
            : 'No expiration'}
          {lastUsed && ` · Last used ${lastUsed}`}
        </Typography>
      </Box>
      {deleteMutation.isPending ? (
        <CircularProgress size={16} />
      ) : (
        <>
          <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
            <MoreVert fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={!!menuAnchor}
            onClose={() => {
              setMenuAnchor(null)
              setConfirmDelete(false)
            }}
          >
            {!confirmDelete ? (
              <MenuItem onClick={() => setConfirmDelete(true)}>
                <ListItemIcon>
                  <Delete fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText>Revoke token</ListItemText>
              </MenuItem>
            ) : (
              <MenuItem onClick={handleDelete}>
                <ListItemIcon>
                  <Delete fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText sx={{ color: 'error.main' }}>Confirm revoke</ListItemText>
              </MenuItem>
            )}
          </Menu>
        </>
      )}
    </Box>
  )
}

const TokenRow = memo(TokenRowRaw)

const OverviewRaw = () => {
  const { t } = useTranslation(['common', 'domain'])

  const [name, setName] = useState('')
  const [expiryDays, setExpiryDays] = useState<number>(0)
  const [generatedToken, setGeneratedToken] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const tokensQuery = useInfiniteQuery({
    ...userQueryKeys.personalAccessTokenList({ userId: 'my' }),
    select: (data) => data.pages.flatMap((page) => page.items),
  })

  const createMutation = usePersonalAccessTokenCreateMutation()

  const handleGenerate = useCallback(() => {
    if (!name.trim()) return

    const now = new Date()
    createMutation.mutate(
      {
        userId: 'my',
        personalAccessTokenInsert: {
          name: name.trim(),
          validFrom: formatISO(now),
          expiryTime: expiryDays > 0 ? formatISO(add(now, { days: expiryDays })) : null,
          allowFullAccess: true,
          scopes: null,
        },
      },
      {
        onSuccess: (data) => {
          setGeneratedToken(data.token!)
          setName('')
          setCopied(false)
        },
      },
    )
  }, [name, expiryDays, createMutation])

  const handleCopy = useCallback(() => {
    setCopied(true)
    setTimeout(() => setCopied(false), 3000)
  }, [])

  return (
    <Box
      sx={{ width: '100%' }}
      component={motion.div}
      variants={popInOut}
      initial="hidden"
      animate="visible"
      exit="hidden"
    >
      <Typography variant="heading-base">Personal Access Tokens</Typography>
      <Typography sx={{ mb: 2 }} variant="text">
        Tokens you have generated that can be used to access the NativeWaves APIs.
      </Typography>

      <AnimatePresence>
        {generatedToken && (
          <Alert sx={{ mb: 2 }} severity="success" onClose={() => setGeneratedToken(null)}>
            <Typography variant="heading-sm" sx={{ mb: 0.5 }}>
              Token created — copy it now, it will not be shown again.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CodeInline sx={{ flex: 1, py: 0.25 }} noWrap>
                {generatedToken}
              </CodeInline>
              <CopyToClipboard text={generatedToken} onCopy={handleCopy}>
                <Tooltip title={copied ? 'Copied!' : 'Copy token'}>
                  <IconButton size="small">
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
              </CopyToClipboard>
            </Box>
          </Alert>
        )}
      </AnimatePresence>

      <Paper sx={{ bgcolor: 'background.levels.500', p: 1.5, mb: 2 }}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          component="form"
          onSubmit={(e) => {
            e.preventDefault()
            handleGenerate()
          }}
        >
          <TextField
            sx={{ flex: 1 }}
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Token name"
            required
          />
          <TextField
            sx={{ width: 160 }}
            size="small"
            select
            value={expiryDays}
            onChange={(e) => setExpiryDays(Number(e.target.value))}
          >
            {EXPIRY_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <LoadingButton
            variant="outlined"
            size="small"
            type="submit"
            endIcon={<AddCircle />}
            loadingPosition="end"
            loading={createMutation.isPending}
            disabled={!name.trim()}
          >
            Generate
          </LoadingButton>
        </Box>
      </Paper>

      {tokensQuery.isLoading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Skeleton height={48} />
          <Skeleton height={48} />
        </Box>
      )}

      {tokensQuery.isSuccess && tokensQuery.data.length > 0 && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="heading-sm">Active tokens</Typography>
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={() => tokensQuery.refetch()}>
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Paper sx={{ bgcolor: 'background.levels.500' }} variant="outlined">
            {tokensQuery.data.map((token) => (
              <TokenRow key={token.id} token={token} />
            ))}
          </Paper>
        </Box>
      )}

      {tokensQuery.isSuccess && tokensQuery.data.length === 0 && !generatedToken && (
        <Typography variant="text" color="text.secondary">
          No tokens yet.
        </Typography>
      )}

      {tokensQuery.isError && <Alert severity="error">{t('somethingWentWrong')}</Alert>}
    </Box>
  )
}

export const Overview = memo(OverviewRaw)
